import { useState } from 'react'
import { ElementType } from 'react'
import { Loader2, Rss, FileText, Mail, MailOpen } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { SidebarMenuButton, useSidebar } from '@/components/ui/sidebar'
import { useReleaseNotes } from './hooks/use-release-notes'
import { tokens } from '@/locales/translations/tokens'

interface ReleaseNotesDropdownProps {
  isActive?: boolean
  icon?: ElementType
}

export function ReleaseNotesDropdown({
  isActive,
  icon,
}: ReleaseNotesDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { i18n, t } = useTranslation()
  const { state } = useSidebar()
  const { notifications, loading, unreadCount, markAsViewed } = useReleaseNotes(
    i18n.language
  )
  const IconComponent = icon || Rss

  const handleItemClick = (actionLink: string | null) => {
    if (actionLink) {
      window.open(actionLink, '_blank')
    }
    setIsOpen(false)
  }

  const handleMarkAsViewed = () => {
    const unreadNotificationIds = notifications
      .filter((notification) => notification.viewed_at === null)
      .map((notification) => notification.external_id)

    if (unreadNotificationIds.length > 0) {
      markAsViewed(unreadNotificationIds)
    }
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)

    if (!open && notifications.length > 0) {
      handleMarkAsViewed()
    }
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          isActive={isActive}
          tooltip={t(tokens.notifications.releaseNotes.tooltip)}
          className={`cursor-pointer ${state === 'collapsed' ? 'relative' : ''}`}
        >
          <IconComponent className='h-4 w-4' />
          <span>{t(tokens.notifications.releaseNotes.title)}</span>
          {unreadCount > 0 && state === 'expanded' && (
            <Badge
              variant='default'
              className='bg-notification-unread ml-auto rounded-full px-1.5 py-0 text-xs text-white hover:bg-red-600'
            >
              {unreadCount}
            </Badge>
          )}
          {unreadCount > 0 && state === 'collapsed' && (
            <div className='absolute right-0.5 top-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-red-500 text-[8px] font-semibold text-white'>
              {unreadCount > 9 ? '9+' : unreadCount}
            </div>
          )}
        </SidebarMenuButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className='w-[450px] p-0'
        align='end'
        side='right'
        sideOffset={8}
      >
        <DropdownMenuLabel className='flex items-center justify-between gap-2 px-4 py-3'>
          <span>{t(tokens.notifications.releaseNotes.title)}</span>
          <div className='flex items-center gap-2'>
            {notifications.length > 0 && (
              <Button onClick={handleMarkAsViewed} variant='outline' size='sm'>
                {unreadCount > 0 ? (
                  <Mail className='h-4 w-4' />
                ) : (
                  <MailOpen className='h-4 w-4' />
                )}
              </Button>
            )}
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {loading ? (
          <div className='flex items-center justify-center py-6'>
            <Loader2 className='h-5 w-5 animate-spin' />
          </div>
        ) : notifications.length === 0 ? (
          <div className='flex h-fit max-h-[60vh] flex-col items-center justify-center px-4 py-12'>
            <FileText className='mb-4 h-12 w-12 text-muted-foreground/50' />
            <h3 className='mb-2 text-sm font-medium text-foreground'>
              {t(tokens.notifications.releaseNotes.empty.title)}
            </h3>
            <p className='max-w-[280px] text-center text-xs text-muted-foreground'>
              {t(tokens.notifications.releaseNotes.empty.description)}
            </p>
          </div>
        ) : (
          <ScrollArea className='h-fit max-h-[60vh] overflow-y-auto'>
            <div className='space-y-0'>
              {notifications.map(
                (notification: Notification, index: number) => (
                  <div key={notification.external_id}>
                    <div
                      className={`cursor-pointer transition-colors hover:bg-accent/50`}
                      onClick={() => handleItemClick(notification.action_link)}
                    >
                      <div className='w-full space-y-3 p-4'>
                        <div className='flex items-center justify-between'>
                          <div className='flex flex-wrap gap-2'>
                            {notification?.notification_tags
                              ?.slice(0, 3)
                              .map((tag, tagIndex) => (
                                <Badge
                                  key={tagIndex}
                                  variant={'outline'}
                                  className='max-w-full truncate rounded-lg px-2 py-1 text-xs font-medium'
                                >
                                  {tag}
                                </Badge>
                              ))}
                            {notification?.notification_tags?.length > 3 && (
                              <Badge
                                variant={'outline'}
                                className='rounded-lg px-2 py-1 text-xs font-medium'
                              >
                                +{notification?.notification_tags?.length - 3}
                              </Badge>
                            )}
                          </div>
                          {notification.viewed_at === null && (
                            <div className='bg-notification-unread h-2 w-2 flex-shrink-0 rounded-full' />
                          )}
                        </div>

                        <h3 className='line-clamp-2 break-words text-[16px] font-medium'>
                          {notification.title}
                        </h3>

                        {notification.description && (
                          <p className='line-clamp-4 break-words text-sm leading-relaxed text-muted-foreground'>
                            {notification.description}
                          </p>
                        )}

                        <div className='mt-3'>
                          <img
                            src={
                              notification.notification_image_url &&
                              notification.notification_image_url.trim() !== ''
                                ? notification.notification_image_url
                                : '/placeholders/blog/banner.png'
                            }
                            alt={notification.title}
                            className='h-48 w-full rounded-md object-cover'
                            loading='lazy'
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              if (
                                target.src !== '/placeholders/blog/banner.png'
                              ) {
                                target.src = '/placeholders/blog/banner.png'
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    {index < notifications.length - 1 && (
                      <DropdownMenuSeparator className='my-0' />
                    )}
                  </div>
                )
              )}
            </div>
          </ScrollArea>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
